import React, { useState, useRef, useEffect } from 'react';
import { FaCamera, FaTrash } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { Document, Page } from 'react-pdf';

import Text from '../Text';

import {
  ContainerFile,
  PreviewGrid,
  PreviewItem,
  RemoveButton,
  PdfPreviewWrapper,
  UploadTrigger,
} from './styles';
import { Row } from 'react-bootstrap';

const MAX_FILE_SIZE = 5450000;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 5;
const ZOOM_STEP = 0.1;

function isPdf(file) {
  return (
    file?.type === 'application/pdf' ||
    file?.name?.toLowerCase().endsWith('.pdf')
  );
}

function ZoomablePreviewItem({ isSingle, onRemove, removeLabel, children }) {
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    function handleWheel(e) {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setZoom(prev => {
        const next = prev + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
        const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, parseFloat(next.toFixed(2))));
        if (clamped === 1) setOffset({ x: 0, y: 0 });
        return clamped;
      });
    }

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  function handleMouseEnter() {
    setHovered(true);
  }

  function handleMouseLeaveItem() {
    setHovered(false);
    dragRef.current = null;
  }

  function handleMouseDown(e) {
    if (zoom <= 1) return;
    e.preventDefault();
    setHintDismissed(true);
    dragRef.current = { startX: e.clientX - offset.x, startY: e.clientY - offset.y };
  }

  function handleMouseMove(e) {
    if (!dragRef.current) return;
    setOffset({
      x: e.clientX - dragRef.current.startX,
      y: e.clientY - dragRef.current.startY,
    });
  }

  function handleMouseUp() {
    dragRef.current = null;
  }

  const isDragging = zoom > 1;
  const showHint = hovered && !hintDismissed && zoom === 1;

  return (
    <PreviewItem
      ref={containerRef}
      $single={isSingle}
      style={{ cursor: isDragging ? (dragRef.current ? 'grabbing' : 'grab') : 'default' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeaveItem}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <div
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
          transformOrigin: 'center center',
          transition: dragRef.current ? 'none' : 'transform 0.1s ease',
          width: '100%',
          userSelect: 'none',
          pointerEvents: 'none',
          filter: showHint ? 'blur(3px)' : 'none',
        }}
      >
        {typeof children === 'function' ? children(zoom) : children}
      </div>

      {showHint && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '12px 16px',
            background: 'rgba(0, 0, 0, 0.45)',
            pointerEvents: 'none',
            userSelect: 'none',
            textAlign: 'center',
          }}
        >
          <span style={{ fontSize: 22 }}>🔍</span>
          <span
            style={{
              color: '#fff',
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 1.5,
              textShadow: '0 1px 3px rgba(0,0,0,0.6)',
            }}
          >
            Segure <kbd style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 3, padding: '1px 5px', fontFamily: 'monospace' }}>Ctrl</kbd> e role o scroll para dar zoom<br />
            Com zoom ativo, arraste para mover
          </span>
        </div>
      )}
      {zoom !== 1 && (
        <div
          style={{
            position: 'absolute',
            bottom: 4,
            left: 6,
            background: 'rgba(0,0,0,0.55)',
            color: '#fff',
            fontSize: 10,
            borderRadius: 3,
            padding: '1px 5px',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {zoom.toFixed(1)}×
        </div>
      )}
      <RemoveButton
        type="button"
        onClick={onRemove}
        title="Remover arquivo"
        aria-label={removeLabel}
      >
        <MdClose size={12} />
      </RemoveButton>
    </PreviewItem>
  );
}

function Upload({
  name,
  id,
  onChange,
  accept = '*',
  label,
  icon,
  error,
  multiple,
  ignoreValidation,
  value,
  onClear,
  clearLabel = 'Remover arquivo',
  maxFileSize = MAX_FILE_SIZE,
  maxFileSizeMessage = `O tamanho máximo permitido para envio de arquivos é de ${Math.round(maxFileSize / 1000000)}MB.`,
  showPreview = false,
  onFilesChange,
  validate,
  onValidationError,
}) {
  const inputRef = useRef();
  const previewFilesRef = useRef([]);

  const [sizeFileGreater, setSizeFileGreater] = useState(false);
  const [fileName, setFileName] = useState('');
  const [previewFiles, setPreviewFiles] = useState([]);

  useEffect(() => {
    previewFilesRef.current = previewFiles;
  }, [previewFiles]);

  useEffect(() => {
    return () => {
      previewFilesRef.current.forEach(({ url }) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);

  function handleFileName(files) {
    if (files?.length > 1) {
      setFileName(`${files?.length} arquivos selecionados`);
    } else {
      const [file] = files;
      setFileName(file?.name);
    }
  }

  function handleChange(event) {
    const files = [...event.target.files];

    if (!files.length) return;

    if (typeof validate === 'function') {
      for (const file of files) {
        if (!validate(file)) {
          if (inputRef.current) inputRef.current.value = '';
          if (typeof onValidationError === 'function') onValidationError(file);
          return;
        }
      }
    }

    const hasFileGreater = files.some(file => file.size > maxFileSize);
    if (!ignoreValidation && hasFileGreater) {
      setSizeFileGreater(true);
      return;
    }

    if (showPreview) {
      previewFilesRef.current.forEach(({ url }) => {
        if (url) URL.revokeObjectURL(url);
      });

      const previews = files.map(file => ({
        file,
        url: URL.createObjectURL(file),
        isPdf: isPdf(file),
      }));
      setPreviewFiles(previews);
    }

    onChange(event, hasFileGreater);
    handleFileName(files);
    setSizeFileGreater(false);
  }

  function handleRemove(index, event) {
    event.stopPropagation();

    const removed = previewFiles[index];
    if (removed?.url) URL.revokeObjectURL(removed.url);

    const next = previewFiles.filter((_, i) => i !== index);
    setPreviewFiles(next);

    if (next.length === 0) {
      setFileName('');
      if (inputRef.current) inputRef.current.value = '';
    } else {
      handleFileName(next.map(p => p.file));
    }

    if (typeof onFilesChange === 'function') {
      onFilesChange(next.map(p => p.file));
    }
  }

  const hasPreview = showPreview && previewFiles.length > 0;
  const isSingle = previewFiles.length === 1;
  const pdfScale = window.devicePixelRatio || 2;

  return (
    <Row style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
      {hasPreview && (
        <ContainerFile display={12}>
          <PreviewGrid $single={isSingle}>
            {previewFiles.map((preview, index) => (
              <ZoomablePreviewItem
                key={`${preview.file.name}-${index}`}
                isSingle={isSingle}
                onRemove={e => handleRemove(index, e)}
                removeLabel={`Remover ${preview.file.name}`}
              >
                {(zoom) =>
                  preview.isPdf ? (
                    <PdfPreviewWrapper>
                      <Document
                        file={preview.url}
                        loading={null}
                        onLoadError={() => { }}
                        onSourceError={() => { }}
                      >
                        <Page
                          pageNumber={1}
                          scale={zoom}
                          devicePixelRatio={(window.devicePixelRatio || 1) * 2}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                      </Document>
                    </PdfPreviewWrapper>
                  ) : (
                    <img
                      src={preview.url}
                      alt={preview.file.name}
                      style={{
                        width: '100%',
                        height: 'auto',
                        imageRendering: 'auto',
                        backfaceVisibility: 'hidden',
                        transform: 'translateZ(0)',
                      }}
                    />
                  )
                }
              </ZoomablePreviewItem>
            ))}
          </PreviewGrid>
        </ContainerFile>
      )}
      <ContainerFile display={12}>
        <input
          type="file"
          ref={inputRef}
          name={name}
          id={id}
          onChange={handleChange}
          accept={accept}
          multiple={multiple}
        />

        <UploadTrigger onClick={() => inputRef.current.click()}>
          {icon || <FaCamera size={30} color="#494950" className="icon" />}

          {error ? (
            <Text color="error" type="label">
              {error}
            </Text>
          ) : sizeFileGreater ? (
            <Text color="error" type="label">
              {maxFileSizeMessage}
            </Text>
          ) : (
            <Text color="#494950" type="label">
              {fileName}
            </Text>
          )}

          <Text color="#494950" type="label">
            {label}
          </Text>
        </UploadTrigger>
      </ContainerFile>
    </Row>
  );
}

export default Upload;
